import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { 
  useOrdine,
  useCambiaStatoOrdine,
  useDeleteOrdine,
  useCambiaStatoSpedizione,
  useConfermaRientroSpedizione,
  useUpdateOrdine,
  useCreatePagamento,
  useUpdatePagamento,
  useAnnullaPagamento,
  useRipristinaOrdine,
} from '@/hooks/useOrdini'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle 
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { verificaSpedizioneOrdine } from '@/api/ordini'
import AllegatiOrdine from '@/components/ordini/AllegatiOrdine'
import DdtButton from '@/components/ordini/DdtButton'
import { buildAssemblyUrl, normalizeAssemblySteps } from '@/utils/assemblyPlan'
import { toast } from 'sonner'
import { 
  ArrowLeft, Pencil, Trash2, XCircle, 
  Package, Truck, CreditCard, Clock, User, MapPin,
  FileText, Printer, MoreVertical, ArrowRightLeft, Factory, AlertTriangle, Plus, Ban, Save,
  RotateCcw, Eye
} from 'lucide-react'

const STATO_COLORS = {
  bozza: 'bg-gray-500',
  confermato: 'bg-blue-500',
  in_preparazione: 'bg-yellow-500',
  preparato: 'bg-orange-500',
  parzialmente_spedito: 'bg-indigo-500',
  spedito: 'bg-purple-500',
  evaso: 'bg-green-500',
  chiuso: 'bg-slate-500',
  annullato: 'bg-red-500',
}

const SPEDIZIONE_STATO_COLORS = {
  preparazione: 'bg-yellow-500',
  pronto: 'bg-orange-500',
  ritirato: 'bg-blue-500',
  in_transito: 'bg-purple-500',
  in_consegna: 'bg-indigo-500',
  consegnato: 'bg-green-500',
  problema: 'bg-red-500',
  reso: 'bg-red-500',
}

const TIPO_LABELS = {
  1: 'Normale',
  2: 'Contenuto Personalizzato',
  3: 'Brandizzazione',
  4: 'Completo',
}

export const METODI_PAGAMENTO = [
  { value: 'contanti', label: 'Contanti' },
  { value: 'bonifico', label: 'Bonifico Bancario' },
  { value: 'carta', label: 'Carta di Credito/Debito' },
  { value: 'assegno', label: 'Assegno' },
  { value: 'sconto', label: 'Sconto' },
]

const STATO_PAGAMENTO_VARIANT = {
  non_pagato: 'destructive',
  parziale: 'warning',
  pagato: 'success',
  omaggio: 'success',
}

const STATO_PAGAMENTO_LABEL = {
  non_pagato: 'Non pagato',
  parziale: 'Parziale',
  pagato: 'Pagato',
  omaggio: 'Omaggio',
}

const toDateTimeLocal = (isoValue) => {
  const date = isoValue ? new Date(isoValue) : new Date()
  if (Number.isNaN(date.getTime())) return ''
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset())
  return date.toISOString().slice(0, 16)
}

const fromDateTimeLocal = (localValue) => {
  if (!localValue) return null
  const date = new Date(localValue)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

export default function OrdineDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  const { data: ordine, isLoading } = useOrdine(id)
  const cambiaStatoMutation = useCambiaStatoOrdine()
  const cambiaStatoSpedizioneMutation = useCambiaStatoSpedizione()
  const confermaRientroMutation = useConfermaRientroSpedizione()
  const deleteMutation = useDeleteOrdine()
  const updateOrdineMutation = useUpdateOrdine()
  const createPagamentoMutation = useCreatePagamento()
  const updatePagamentoMutation = useUpdatePagamento()
  const annullaPagamentoMutation = useAnnullaPagamento()
  const ripristinaMutation = useRipristinaOrdine()
  
  const [showAnnullaDialog, setShowAnnullaDialog] = useState(false)
  const [showRipristinaDialog, setShowRipristinaDialog] = useState(false)
  const [causaleAnnullamento, setCausaleAnnullamento] = useState('')
  const [cambiaStatoKey, setCambiaStatoKey] = useState(0)
  const [isCheckingSpedizione, setIsCheckingSpedizione] = useState(false)
  const [assemblyDialog, setAssemblyDialog] = useState({ open: false, result: null })
  const autoOpenedAssemblyRef = useRef(false)
  const [pagamentoDialogOpen, setPagamentoDialogOpen] = useState(false)
  const [editingPagamento, setEditingPagamento] = useState(null)
  const [annullaPagamentoDialog, setAnnullaPagamentoDialog] = useState({ open: false, pagamento: null })
  const [causaleAnnullaPagamento, setCausaleAnnullaPagamento] = useState('')
  const [pagamentoForm, setPagamentoForm] = useState({
    metodo: 'bonifico',
    importo: '',
    data_pagamento: toDateTimeLocal(),
    riferimento: '',
    note: '',
  })

  const STATI_LIBERO = ['bozza']
  const STATI_RISERVATO = ['confermato', 'in_preparazione', 'preparato']
  const STATI_SCARICATO = ['parzialmente_spedito', 'spedito', 'evaso', 'chiuso']

  const getGruppoGiacenza = (stato) => {
    if (STATI_LIBERO.includes(stato)) return 'libero'
    if (STATI_RISERVATO.includes(stato)) return 'riservato'
    if (STATI_SCARICATO.includes(stato)) return 'scaricato'
    return null
  }

  const handleAnnulla = () => {
    if (causaleAnnullamento.length < 10) {
      return
    }
    cambiaStatoMutation.mutate(
      { id, data: { stato: 'annullato', causale: causaleAnnullamento } },
      {
        onSuccess: () => {
          setShowAnnullaDialog(false)
          setCausaleAnnullamento('')
        }
      }
    )
  }

  const handleRipristina = () => {
    ripristinaMutation.mutate(id, {
      onSuccess: () => setShowRipristinaDialog(false),
    })
  }

  const handleCambiaStato = (nuovoStato) => {
    // Resetta il Select "Cambia stato" al placeholder dopo l'interazione,
    // sia se l'azione viene confermata sia se annullata.
    setCambiaStatoKey(k => k + 1)

    const gruppoAttuale = getGruppoGiacenza(ordine.stato)
    const gruppoNuovo = getGruppoGiacenza(nuovoStato)
    const label = nuovoStato.replace(/_/g, ' ')
    
    // Conferma solo per transizioni che impattano giacenze
    if (gruppoAttuale !== gruppoNuovo) {
      let msg = ''
      if (gruppoAttuale === 'libero' && gruppoNuovo === 'riservato')
        msg = `Passare a "${label}"? Il fabbisogno verrà registrato. La preparazione richiede materiali disponibili.`
      else if (gruppoAttuale === 'riservato' && gruppoNuovo === 'scaricato')
        msg = `Passare a "${label}"? I prodotti verranno scaricati dal magazzino (venduti).`
      else if (gruppoAttuale === 'scaricato' && gruppoNuovo === 'riservato')
        msg = `Tornare a "${label}"? I prodotti rientreranno in magazzino come riservati.`
      else if (gruppoAttuale === 'riservato' && gruppoNuovo === 'libero')
        msg = `Tornare a "${label}"? Le giacenze riservate verranno liberate.`
      else if (gruppoAttuale === 'scaricato' && gruppoNuovo === 'libero')
        msg = `Tornare a "${label}"? I prodotti rientreranno in magazzino come disponibili.`
      else if (gruppoAttuale === 'libero' && gruppoNuovo === 'scaricato')
        msg = `Passare a "${label}"? I prodotti verranno scaricati direttamente dal magazzino.`
      
      if (msg && !confirm(msg)) return
    }
    
    cambiaStatoMutation.mutate({ id, data: { stato: nuovoStato } })
  }

  const handleCambiaStatoSpedizione = (spedizioneId, nuovoStato) => {
    cambiaStatoSpedizioneMutation.mutate({ id: spedizioneId, stato: nuovoStato })
  }

  const handleConfermaRientro = (spedizioneId) => {
    const causale = window.prompt('Confermi che tutti i colli sono fisicamente rientrati? Indica la causale del rientro:')
    if (!causale?.trim()) return
    if (!window.confirm('La merce rientrerà in magazzino e sarà riservata per questo ordine. Confermi?')) return
    confermaRientroMutation.mutate({ id: spedizioneId, causale: causale.trim() })
  }

  const handleDelete = () => {
    if (confirm('Sei sicuro di voler eliminare questo ordine?')) {
      deleteMutation.mutate(id, {
        onSuccess: () => navigate('/ordini')
      })
    }
  }

  const verificaPrerequisitiSpedizione = async ({ navigateOnSuccess = false, silentSuccess = false } = {}) => {
    setIsCheckingSpedizione(true)
    try {
      const response = await verificaSpedizioneOrdine(id)
      const risultato = response?.data || {}

      if (!risultato.ok) {
        const steps = normalizeAssemblySteps(risultato)
        const primoStep = steps[0]
        const dettaglioMancanza = primoStep
          ? `${primoStep.tipologia_box_nome}: da assemblare ${primoStep.da_assemblare}` +
            (steps.length > 1 ? ` (poi altri ${steps.length - 1} step)` : '')
          : null
        toast.error(dettaglioMancanza || risultato.message || 'Prerequisiti spedizione non rispettati.')
        setAssemblyDialog({ open: true, result: risultato })
        return false
      }

      setAssemblyDialog({ open: false, result: null })
      if (navigateOnSuccess) {
        navigate(`/ordini/${id}/spedizione/wizard`)
      } else if (!silentSuccess) {
        toast.success('Prerequisiti spedizione OK. Puoi procedere.')
      }
      return true
    } catch (error) {
      const errorMsg =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        'Errore durante la verifica dei prerequisiti spedizione.'
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Errore durante la verifica spedizione.')
      return false
    } finally {
      setIsCheckingSpedizione(false)
    }
  }

  const handleApriWizardSpedizione = async () => {
    await verificaPrerequisitiSpedizione({ navigateOnSuccess: true })
  }

  useEffect(() => {
    if (!ordine || autoOpenedAssemblyRef.current) return
    if (searchParams.get('assembly_plan') !== '1') return

    autoOpenedAssemblyRef.current = true
    verificaPrerequisitiSpedizione({ navigateOnSuccess: false, silentSuccess: true }).finally(() => {
      const next = new URLSearchParams(searchParams)
      next.delete('assembly_plan')
      setSearchParams(next, { replace: true })
    })
  }, [ordine, searchParams, setSearchParams])

  const assemblySteps = normalizeAssemblySteps(assemblyDialog.result)
  const overbookingRows = Array.isArray(assemblyDialog.result?.mancanze_overbooking)
    ? assemblyDialog.result.mancanze_overbooking
    : []
  const pagamentiAttivi = (ordine?.pagamenti || []).filter((p) => p.stato === 'attivo')
  const totalePagatoCents = ordine?.totale_pagato_cents || 0
  const scontiPagamentoCents = ordine?.sconti_pagamento_cents || 0
  const residuoPagamentoCents = ordine?.residuo_pagamento_cents || 0
  const totaleOrdineCents = Math.round((ordine?.totale || 0) * 100)

  const getLimitePagamentoCents = (pagamentoCorrente = null) => {
    if (!ordine) return 0
    if (!pagamentoCorrente || pagamentoCorrente.stato !== 'attivo') {
      return residuoPagamentoCents
    }
    return residuoPagamentoCents + (pagamentoCorrente.importo_cents || 0)
  }

  const resetPagamentoForm = (pagamento = null) => {
    setEditingPagamento(pagamento)
    setPagamentoForm({
      metodo: pagamento?.metodo || 'bonifico',
      importo: pagamento ? String(((pagamento.importo_cents || 0) / 100).toFixed(2)) : '',
      data_pagamento: toDateTimeLocal(pagamento?.data_pagamento),
      riferimento: pagamento?.riferimento || '',
      note: pagamento?.note || '',
    })
  }

  const handleOpenNuovoPagamento = () => {
    if (ordine.is_omaggio) {
      toast.error('Ordine omaggio: i pagamenti sono disabilitati.')
      return
    }
    resetPagamentoForm(null)
    setPagamentoDialogOpen(true)
  }

  const handleOpenModificaPagamento = (pagamento) => {
    if (ordine.is_omaggio) {
      toast.error('Ordine omaggio: i pagamenti sono disabilitati.')
      return
    }
    if (pagamento.stato !== 'attivo') {
      toast.error('Puoi modificare solo pagamenti attivi.')
      return
    }
    resetPagamentoForm(pagamento)
    setPagamentoDialogOpen(true)
  }

  const handleSalvaPagamento = () => {
    const importoEuro = Number.parseFloat((pagamentoForm.importo || '').replace(',', '.'))
    if (!Number.isFinite(importoEuro) || importoEuro <= 0) {
      toast.error('Inserisci un importo valido maggiore di zero.')
      return
    }

    const importoCents = Math.round(importoEuro * 100)
    const limiteCents = getLimitePagamentoCents(editingPagamento)
    if (importoCents > limiteCents) {
      toast.error(`Importo eccedente: massimo consentito ${((limiteCents || 0) / 100).toFixed(2)} €.`)
      return
    }

    const dataPagamentoIso = fromDateTimeLocal(pagamentoForm.data_pagamento)
    if (!dataPagamentoIso) {
      toast.error('Inserisci una data pagamento valida.')
      return
    }
    if (new Date(dataPagamentoIso).getTime() > Date.now()) {
      toast.error('La data pagamento non puo essere futura.')
      return
    }

    const payload = {
      ordine: ordine.id,
      metodo: pagamentoForm.metodo,
      importo_cents: importoCents,
      data_pagamento: dataPagamentoIso,
      riferimento: pagamentoForm.riferimento || '',
      note: pagamentoForm.note || '',
    }

    const onSuccess = () => {
      setPagamentoDialogOpen(false)
      resetPagamentoForm(null)
    }

    if (editingPagamento) {
      updatePagamentoMutation.mutate({ id: editingPagamento.id, data: payload }, { onSuccess })
      return
    }

    createPagamentoMutation.mutate(payload, { onSuccess })
  }

  const handleOpenAnnullaPagamento = (pagamento) => {
    if (pagamento.stato !== 'attivo') {
      toast.error('Pagamento gia annullato.')
      return
    }
    setCausaleAnnullaPagamento('')
    setAnnullaPagamentoDialog({ open: true, pagamento })
  }

  const handleConfermaAnnullaPagamento = () => {
    if (causaleAnnullaPagamento.trim().length < 3) {
      toast.error('Inserisci una causale di almeno 3 caratteri.')
      return
    }
    const pagamento = annullaPagamentoDialog.pagamento
    if (!pagamento) return
    annullaPagamentoMutation.mutate(
      { id: pagamento.id, data: { causale: causaleAnnullaPagamento.trim() } },
      {
        onSuccess: () => {
          setAnnullaPagamentoDialog({ open: false, pagamento: null })
          setCausaleAnnullaPagamento('')
        },
      }
    )
  }

  const handleToggleOmaggio = (checked) => {
    if (!ordine) return
    if (checked && pagamentiAttivi.length > 0) {
      toast.error('Impossibile impostare omaggio: esistono pagamenti attivi.')
      return
    }

    updateOrdineMutation.mutate({
      id,
      data: { is_omaggio: checked },
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0)
  }

  const formatCurrencyCents = (value) => formatCurrency((value || 0) / 100)
  const limitePagamentoCorrenteCents = getLimitePagamentoCents(editingPagamento)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    )
  }

  if (!ordine) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Ordine non trovato</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/ordini')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold font-mono">{ordine.numero}</h1>
              <Badge className={`${STATO_COLORS[ordine.stato]} text-white`}>
                {ordine.stato_display}
              </Badge>
              {ordine.stato_suggerito_da_spedizioni && ordine.stato_suggerito_da_spedizioni !== ordine.stato && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">→</span>
                  <Badge variant="outline" className="border-blue-500 text-blue-700">
                    Suggerito: {ordine.stato_suggerito_da_spedizioni}
                  </Badge>
                </div>
              )}
            </div>
            <p className="text-muted-foreground">
              {TIPO_LABELS[ordine.tipo]} • Creato il {formatDate(ordine.data_ordine)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {ordine.is_modificabile && (
            <Button variant="outline" onClick={() => navigate(`/ordini/${id}/modifica`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Modifica
            </Button>
          )}
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Stampa
          </Button>
          {ordine.stato === 'bozza' && (
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Elimina
            </Button>
          )}
        </div>
      </div>

      {/* Azioni Stato */}
      {(ordine.transizioni_permesse?.length > 0 || ordine.is_spedibile || ordine.is_ripristinabile) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5" />
              Azioni
            </CardTitle>
          </CardHeader>
          <CardContent className="flex gap-3 flex-wrap items-center">
            {/* Cambio stato manuale (spedito/parzialmente_spedito sono automatici) */}
            {ordine.transizioni_permesse?.filter(s => 
              s !== 'annullato' && s !== 'parzialmente_spedito' && s !== 'spedito'
            ).length > 0 && (() => {
              const statiDisponibili = ordine.transizioni_permesse.filter(s => 
                s !== 'annullato' && s !== 'parzialmente_spedito' && s !== 'spedito'
              )

              return (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Cambia stato:</span>
                  <Select
                    key={cambiaStatoKey}
                    onValueChange={(value) => handleCambiaStato(value)}
                    disabled={cambiaStatoMutation.isPending}
                  >
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Seleziona stato..." />
                    </SelectTrigger>
                    <SelectContent>
                      {statiDisponibili.map((stato) => (
                        <SelectItem key={stato} value={stato}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${STATO_COLORS[stato]}`} />
                            <span>{stato.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )
            })()}

            <Separator orientation="vertical" className="h-8 hidden md:block" />

            {/* Wizard Spedizioni */}
            {ordine.is_spedibile && !ordine.is_completamente_spedito && (
              <Button 
                variant="default"
                onClick={handleApriWizardSpedizione}
                disabled={isCheckingSpedizione}
                className="bg-green-600 hover:bg-green-700"
              >
                <Truck className="mr-2 h-4 w-4" />
                {isCheckingSpedizione ? 'Verifica in corso...' : 'Crea Spedizione'}
              </Button>
            )}
            {ordine.is_completamente_spedito && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  ✅ Ordine completamente spedito
                </p>
              </div>
            )}

            {/* Annulla */}
            {ordine.is_annullabile && (
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => setShowAnnullaDialog(true)}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Annulla
              </Button>
            )}
            {ordine.ha_merce_spedita_non_rientrata && (
              <p className="text-sm text-amber-700">
                Annullamento bloccato: conferma prima il rientro fisico di tutte le spedizioni.
              </p>
            )}

            {ordine.is_ripristinabile && (
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowRipristinaDialog(true)}
                disabled={ripristinaMutation.isPending}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Ripristina ordine
              </Button>
            )}

            {/* Info spedizioni bloccano stato */}
            {ordine.spedizioni?.length > 0 && ordine.stato !== 'annullato' && ordine.stato !== 'chiuso' && (
              <p className="text-xs text-muted-foreground">
                ⚠️ Ordine con spedizioni: non può tornare a bozza/confermato/in preparazione
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        {/* Cliente */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="font-medium text-lg">{ordine.cliente_nome}</p>
            {ordine.cliente_email && (
              <p className="text-sm text-muted-foreground">{ordine.cliente_email}</p>
            )}
            {ordine.cliente_telefono && (
              <p className="text-sm text-muted-foreground">{ordine.cliente_telefono}</p>
            )}
            <Button 
              variant="link" 
              className="p-0 h-auto"
              onClick={() => navigate(`/clienti/${ordine.cliente.id}`)}
            >
              Vai al cliente →
            </Button>
          </CardContent>
        </Card>

        {/* Venditore */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Venditore
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {ordine.venditore_nome ? (
              <>
                <p className="font-medium text-lg">{ordine.venditore_nome}</p>
                {ordine.venditore?.telefono && (
                  <p className="text-sm text-muted-foreground">{ordine.venditore.telefono}</p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Nessun venditore assegnato</p>
            )}
          </CardContent>
        </Card>

        {/* Indirizzo Spedizione */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Indirizzo Spedizione
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ordine.indirizzo_spedizione_completo ? (
              <p className="text-sm whitespace-pre-line">{ordine.indirizzo_spedizione_completo}</p>
            ) : (
              <p className="text-sm text-muted-foreground">Nessun indirizzo selezionato</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Righe Ordine */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Prodotti / Box
          </CardTitle>
          <div className="rounded-lg border bg-muted/40 px-4 py-2 text-right">
            <p className="text-xs text-muted-foreground">Box residui da spedire</p>
            <p className="text-2xl font-bold">
              {ordine.riepilogo_spedizione?.box_residue ?? 0}
            </p>
            <p className="text-xs text-muted-foreground">
              {ordine.riepilogo_spedizione?.box_spedite ?? 0} spediti su{' '}
              {ordine.riepilogo_spedizione?.box_ordinate ?? 0}
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {ordine.righe?.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Nessun prodotto</p>
          ) : (
            <div className="space-y-3">
              {ordine.righe?.map((riga) => (
                <div
                  key={riga.id} 
                  className="p-3 border rounded-lg"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="font-medium">{riga.descrizione}</p>
                      <p className="text-sm text-muted-foreground">Cod: {riga.codice}</p>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <Badge variant="outline">Ordinati: {riga.quantita}</Badge>
                        <Badge variant="secondary">
                          Spediti: {riga.quantita_spedita ?? 0}
                        </Badge>
                        <Badge variant={(riga.quantita_residua ?? riga.quantita) > 0 ? 'default' : 'outline'}>
                          Da spedire: {riga.quantita_residua ?? riga.quantita}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="font-medium">{formatCurrency(riga.totale)}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatCurrency(riga.prezzo_unitario)} cad.
                      </p>
                    </div>
                  </div>

                  {riga.allocazioni_spedizioni?.length > 0 && (
                    <div className="mt-3 border-t pt-3">
                      <p className="mb-2 text-xs font-medium text-muted-foreground">
                        Dettaglio quantità spedite
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {riga.allocazioni_spedizioni.map((allocazione) => (
                          <button
                            key={`${allocazione.spedizione_id}-${allocazione.collo_id}`}
                            type="button"
                            onClick={() => navigate(`/ordini/spedizioni/${allocazione.spedizione_id}`)}
                            className="rounded-md border bg-muted/40 px-2.5 py-1.5 text-left text-xs hover:border-primary hover:bg-muted"
                            title="Apri il dettaglio della spedizione"
                          >
                            <span className="font-mono font-medium">
                              {allocazione.spedizione_codice || `Spedizione #${allocazione.spedizione_id}`}
                            </span>
                            {' · '}{allocazione.collo_codice}
                            {' · '}Qtà {allocazione.quantita}
                            <span className="ml-1 text-muted-foreground">
                              ({allocazione.spedizione_stato_display})
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <Separator className="my-4" />

          {/* Totali */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotale prodotti</span>
              <span>{formatCurrency(ordine.totale_prodotti)}</span>
            </div>
            {ordine.totale_sconti > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Sconti</span>
                <span>-{formatCurrency(ordine.totale_sconti)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span>Spedizione</span>
              <span>{formatCurrency(ordine.totale_spedizione)}</span>
            </div>
            <Separator />
            <div className="flex justify-between font-bold text-lg">
              <span>Totale</span>
              <span>{formatCurrency(ordine.totale)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Spedizioni con Colli (FASE 7.4) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Spedizioni
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ordine.spedizioni?.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Nessuna spedizione</p>
            ) : (
              <div className="space-y-4">
                {ordine.spedizioni?.map((spedizione) => (
                  <div key={spedizione.id} className="p-3 border rounded-lg space-y-3">
                    {/* Header Spedizione */}
                    <div className="flex justify-between items-start gap-3">
                      <div>
                        <button
                          type="button"
                          onClick={() => navigate(`/ordini/spedizioni/${spedizione.id}`)}
                          className="font-mono font-medium text-primary hover:underline"
                        >
                          {spedizione.codice || `Spedizione #${spedizione.id}`}
                        </button>
                        <p className="text-xs text-muted-foreground">{spedizione.corriere_display}</p>
                        {spedizione.tracking_number && (
                          <a 
                            href={spedizione.tracking_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm font-mono text-blue-600 hover:underline"
                          >
                            {spedizione.tracking_number}
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <Badge className={`${SPEDIZIONE_STATO_COLORS[spedizione.stato]} text-white`}>
                            {spedizione.stato_display}
                          </Badge>
                          {spedizione.numero_colli > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {spedizione.numero_colli} colli • {spedizione.peso_totale_kg} kg
                            </p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/ordini/spedizioni/${spedizione.id}`)}
                          title="Apri dettaglio spedizione"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <DdtButton spedizione={spedizione} variant="outline" size="sm" />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleCambiaStatoSpedizione(spedizione.id, 'preparazione')}>
                              Preparazione
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCambiaStatoSpedizione(spedizione.id, 'pronto')}>
                              Pronto
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCambiaStatoSpedizione(spedizione.id, 'ritirato')}>
                              Ritirato
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCambiaStatoSpedizione(spedizione.id, 'in_transito')}>
                              In Transito
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCambiaStatoSpedizione(spedizione.id, 'consegnato')}>
                              Consegnato
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCambiaStatoSpedizione(spedizione.id, 'problema')}>
                              Problemi
                            </DropdownMenuItem>
                            {spedizione.stato === 'problema' && (
                              <DropdownMenuItem onClick={() => handleConfermaRientro(spedizione.id)}>
                                Conferma rientro fisico
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    
                    {/* Indirizzo Spedizione */}
                    {spedizione.indirizzo_via && (
                      <div className="text-sm text-muted-foreground flex items-start gap-2">
                        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span className="whitespace-pre-line">{spedizione.indirizzo_completo}</span>
                      </div>
                    )}

                    {spedizione.note?.trim() && (
                      <div className="text-sm">
                        <p className="font-medium">Note spedizione</p>
                        <p className="whitespace-pre-wrap text-muted-foreground">{spedizione.note}</p>
                      </div>
                    )}
                    
                    {/* Lista Colli (FASE 7.4) */}
                    {spedizione.colli?.length > 0 && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                          <Package className="h-3 w-3" />
                          Colli
                        </p>
                        <div className="space-y-2">
                          {spedizione.colli.map((collo) => (
                            <div
                              key={collo.id} 
                              className="rounded bg-muted/50 p-2 text-sm"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs bg-background px-1.5 py-0.5 rounded">
                                    {collo.codice_collo}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <User className="h-3 w-3" />
                                    {collo.destinatario_nome}
                                  </span>
                                </div>
                                <span className="text-muted-foreground">
                                  {collo.peso_kg} kg
                                </span>
                              </div>
                              {collo.righe_ordine?.length > 0 && (
                                <div className="mt-2 space-y-1 border-t pt-2">
                                  {collo.righe_ordine.map((contenuto) => (
                                    <div
                                      key={contenuto.id}
                                      className="flex justify-between gap-3 text-xs"
                                    >
                                      <span>
                                        {contenuto.riga_ordine_codice} ·{' '}
                                        {contenuto.riga_ordine_descrizione}
                                      </span>
                                      <span className="shrink-0 font-medium">
                                        Qtà {contenuto.quantita}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagamenti */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Pagamenti
            </CardTitle>
            <Button
              size="sm"
              onClick={handleOpenNuovoPagamento}
              disabled={ordine.is_omaggio || createPagamentoMutation.isPending}
            >
              <Plus className="mr-2 h-4 w-4" />
              Aggiungi
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">Stato pagamento</span>
                <Badge variant={STATO_PAGAMENTO_VARIANT[ordine.stato_pagamento] || 'secondary'}>
                  {ordine.stato_pagamento === 'pagato' && (scontiPagamentoCents > 0 || ordine.totale_cents === 0)
                    ? 'Saldato'
                    : STATO_PAGAMENTO_LABEL[ordine.stato_pagamento] || ordine.stato_pagamento}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded border p-2 bg-background">
                  <p className="text-xs text-muted-foreground">Totale ordine</p>
                  <p className="font-semibold">{formatCurrencyCents(totaleOrdineCents)}</p>
                </div>
                <div className="rounded border p-2 bg-background">
                  <p className="text-xs text-muted-foreground">Incassato</p>
                  <p className="font-semibold">{formatCurrencyCents(totalePagatoCents)}</p>
                </div>
                {scontiPagamentoCents > 0 && (
                  <div className="rounded border p-2 bg-background col-span-2">
                    <p className="text-xs text-muted-foreground">Sconti registrati</p>
                    <p className="font-semibold">{formatCurrencyCents(scontiPagamentoCents)}</p>
                  </div>
                )}
                <div className="rounded border p-2 bg-background col-span-2">
                  <p className="text-xs text-muted-foreground">Residuo</p>
                  <p className="font-semibold">{formatCurrencyCents(residuoPagamentoCents)}</p>
                </div>
              </div>

              <div className="pt-2 border-t flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Ordine omaggio</p>
                  <p className="text-xs text-muted-foreground">
                    Se attivo, la registrazione pagamenti viene bloccata.
                  </p>
                </div>
                <Switch
                  checked={Boolean(ordine.is_omaggio)}
                  onCheckedChange={handleToggleOmaggio}
                  disabled={
                    updateOrdineMutation.isPending ||
                    ordine.stato === 'annullato' ||
                    ordine.stato === 'chiuso'
                  }
                />
              </div>
            </div>

            {ordine.pagamenti?.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Nessun pagamento</p>
            ) : (
              <div className="space-y-3">
                {ordine.pagamenti?.map((pagamento) => (
                  <div key={pagamento.id} className="p-3 border rounded-lg space-y-2">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <p className="font-medium">{pagamento.metodo_display || pagamento.metodo}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(pagamento.data_pagamento)}
                        </p>
                        {pagamento.operatore_nome && (
                          <p className="text-xs text-muted-foreground">
                            Operatore: {pagamento.operatore_nome}
                          </p>
                        )}
                        {pagamento.causale_annullamento && (
                          <p className="text-xs text-red-700">
                            Causale annullamento: {pagamento.causale_annullamento}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrencyCents(pagamento.importo_cents)}</p>
                        <Badge variant={pagamento.stato === 'attivo' ? 'success' : 'destructive'}>
                          {pagamento.stato_display}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex justify-between items-center gap-2 text-xs text-muted-foreground">
                      <span>Rif: {pagamento.riferimento || '-'}</span>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenModificaPagamento(pagamento)}
                          disabled={pagamento.stato !== 'attivo' || ordine.is_omaggio}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Modifica
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenAnnullaPagamento(pagamento)}
                          disabled={pagamento.stato !== 'attivo'}
                        >
                          <Ban className="h-4 w-4 mr-1" />
                          Annulla
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Note */}
      {(ordine.note || ordine.note_interne) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Note
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {ordine.note && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Note Cliente</p>
                <p className="text-sm whitespace-pre-wrap">{ordine.note}</p>
              </div>
            )}
            {ordine.note_interne && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Note Interne</p>
                <p className="text-sm whitespace-pre-wrap">{ordine.note_interne}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Storico Stati */}
      {ordine.storico_stati?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Storico Stati
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {ordine.storico_stati?.map((storico) => (
                <div key={storico.id} className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground w-32">
                    {formatDate(storico.created_at)}
                  </span>
                  <span>
                    {storico.stato_precedente} → <strong>{storico.stato_nuovo}</strong>
                  </span>
                  {storico.utente_nome && (
                    <span className="text-muted-foreground">
                      ({storico.utente_nome})
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Allegati ordine (Punto 15 feedback cliente) */}
      {ordine?.id && <AllegatiOrdine ordineId={ordine.id} />}

      {/* Dialog Creazione/Modifica Pagamento */}
      <Dialog
        open={pagamentoDialogOpen}
        onOpenChange={(open) => {
          setPagamentoDialogOpen(open)
          if (!open) resetPagamentoForm(null)
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPagamento ? 'Modifica registrazione' : 'Nuova registrazione'}
            </DialogTitle>
            <DialogDescription>
              Residuo disponibile: {formatCurrencyCents(limitePagamentoCorrenteCents)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium mb-1">Metodo pagamento</p>
              <Select
                value={pagamentoForm.metodo}
                onValueChange={(value) => setPagamentoForm((prev) => ({ ...prev, metodo: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona metodo" />
                </SelectTrigger>
                <SelectContent>
                  {METODI_PAGAMENTO.map((metodo) => (
                    <SelectItem key={metodo.value} value={metodo.value}>
                      {metodo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {pagamentoForm.metodo === 'sconto' && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Riduce il residuo dell&apos;ordine, ma non aumenta l&apos;incassato.
                </p>
              )}
            </div>

            <div>
              <p className="text-sm font-medium mb-1">Importo (€)</p>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={pagamentoForm.importo}
                onChange={(e) => setPagamentoForm((prev) => ({ ...prev, importo: e.target.value }))}
                placeholder="0,00"
              />
            </div>

            <div>
              <p className="text-sm font-medium mb-1">
                {pagamentoForm.metodo === 'sconto' ? 'Data sconto' : 'Data pagamento'}
              </p>
              <Input
                type="datetime-local"
                value={pagamentoForm.data_pagamento}
                onChange={(e) => setPagamentoForm((prev) => ({ ...prev, data_pagamento: e.target.value }))}
              />
            </div>

            <div>
              <p className="text-sm font-medium mb-1">Riferimento</p>
              <Input
                value={pagamentoForm.riferimento}
                onChange={(e) => setPagamentoForm((prev) => ({ ...prev, riferimento: e.target.value }))}
                placeholder="CRO, n. assegno, POS..."
              />
            </div>

            <div>
              <p className="text-sm font-medium mb-1">Note</p>
              <Textarea
                rows={3}
                value={pagamentoForm.note}
                onChange={(e) => setPagamentoForm((prev) => ({ ...prev, note: e.target.value }))}
                placeholder="Note facoltative"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPagamentoDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={handleSalvaPagamento}
              disabled={createPagamentoMutation.isPending || updatePagamentoMutation.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {editingPagamento
                ? 'Salva modifiche'
                : pagamentoForm.metodo === 'sconto' ? 'Registra sconto' : 'Registra pagamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Annullamento Pagamento */}
      <Dialog
        open={annullaPagamentoDialog.open}
        onOpenChange={(open) => setAnnullaPagamentoDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Annulla pagamento</DialogTitle>
            <DialogDescription>
              Questa azione mantiene lo storico audit del pagamento.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Inserisci la causale di annullamento (minimo 3 caratteri).
            </p>
            <Textarea
              value={causaleAnnullaPagamento}
              onChange={(e) => setCausaleAnnullaPagamento(e.target.value)}
              placeholder="Es. registrazione duplicata"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAnnullaPagamentoDialog({ open: false, pagamento: null })}
            >
              Chiudi
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfermaAnnullaPagamento}
              disabled={annullaPagamentoMutation.isPending}
            >
              <Ban className="mr-2 h-4 w-4" />
              Conferma annullamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Piano Assemblaggio */}
      <Dialog
        open={assemblyDialog.open}
        onOpenChange={(open) => setAssemblyDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Spedizione bloccata: serve assemblaggio
            </DialogTitle>
            <DialogDescription>
              Per l&apos;ordine {ordine.numero} devi completare questi step in sequenza.
            </DialogDescription>
          </DialogHeader>

          {assemblySteps.length > 0 ? (
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
              {assemblySteps.map((step) => (
                <div key={`${step.step}-${step.tipologia_box_id}`} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-medium">
                        Step {step.step}: {step.tipologia_box_nome}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Disponibili: {step.assemblate_disponibili} • Richieste totali: {step.richieste_totali}
                      </p>
                    </div>
                    <Badge variant={step.da_assemblare > 0 ? 'destructive' : 'secondary'}>
                      Da assemblare: {step.da_assemblare}
                    </Badge>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(
                          buildAssemblyUrl({
                            step,
                            ordineNumero: ordine.numero,
                            returnTo: `/ordini/${id}?assembly_plan=1`,
                          })
                        )
                      }
                    >
                      <Factory className="h-4 w-4 mr-2" />
                      Assembla questo step
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {assemblyDialog.result?.message || 'Non ci sono dettagli sul piano di assemblaggio.'}
              </p>
              {overbookingRows.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1">
                  <p className="text-sm font-medium text-amber-800">Overbooking attivo</p>
                  {overbookingRows.map((row) => (
                    <p key={`${row.tipologia_box_id}-${row.riga_ordine_id}`} className="text-xs text-amber-800">
                      {row.tipologia_box_nome}: effettiva {row.quantita_effettiva} (disp {row.assemblate_disponibili}, ris {row.quantita_riservata})
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setAssemblyDialog({ open: false, result: null })}
            >
              Chiudi
            </Button>
            <Button
              onClick={() => verificaPrerequisitiSpedizione({ navigateOnSuccess: false })}
              disabled={isCheckingSpedizione}
            >
              {isCheckingSpedizione ? 'Verifica in corso...' : 'Ho assemblato, ricontrolla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Annullamento */}
      <Dialog open={showAnnullaDialog} onOpenChange={setShowAnnullaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annulla Ordine</DialogTitle>
            <DialogDescription>
              Inserisci la causale dell&apos;annullamento (minimo 10 caratteri).
              Lo stock riservato verrà ripristinato.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Causale annullamento..."
            value={causaleAnnullamento}
            onChange={(e) => setCausaleAnnullamento(e.target.value)}
            rows={3}
          />
          {causaleAnnullamento.length > 0 && causaleAnnullamento.length < 10 && (
            <p className="text-sm text-red-500">
              Minimo 10 caratteri ({causaleAnnullamento.length}/10)
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnnullaDialog(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleAnnulla}
              disabled={causaleAnnullamento.length < 10 || cambiaStatoMutation.isPending}
            >
              {cambiaStatoMutation.isPending ? 'Annullamento...' : 'Conferma Annullamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showRipristinaDialog} onOpenChange={setShowRipristinaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ripristina ordine</DialogTitle>
            <DialogDescription>
              L&apos;ordine {ordine.numero} tornerà allo stato{' '}
              <strong>
                {(ordine.stato_pre_annullamento || '').replace(/_/g, ' ')}
              </strong>.
              Le disponibilità e le riserve verranno ricostruite; se lo stock
              bloccante non è sufficiente, non verrà applicata alcuna modifica.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRipristinaDialog(false)}
              disabled={ripristinaMutation.isPending}
            >
              Annulla
            </Button>
            <Button onClick={handleRipristina} disabled={ripristinaMutation.isPending}>
              {ripristinaMutation.isPending ? 'Ripristino...' : 'Conferma ripristino'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
